import React, { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { LoaderCircle, File, Folder, CornerLeftUp, Share2 } from 'lucide-react'
import FileTypeIcon from '../../../components/file-manager/FileIcon'
import { useTranslation } from 'react-i18next'
import { formatBytes, formatDate } from '../../../utils/formatters'
import Grid from '../../../components/grid'
import { getPreviewFileType } from '@/components/file-preview'
import { canPreviewDocument } from '../utils/document-preview'
import { levelAtLeast } from '../utils/permission-levels'

const rowLevelAtLeast = (folder: any, min: string) =>
  levelAtLeast(folder?.effectivePermission?.level, min)

interface FolderType {
  id: string
  name: string
  description: string | null
  parentId: string | null
  documentCount: number
  activeShareLinkCount: number
}

interface Document {
  id: string
  name: string
  description: string | null
  filename: string
  mimetype: string
  size: number
  revisionCount: number
  activeShareLinkCount: number
  createdAt: string
  updatedAt: string
  previewUrl: string | null
  createdBy: {
    id: string
    name: string
  }
  currentRevision?: {
    id: string
    version: number
    uploadedAt: string
    comment: string | null
    fileId: string
    previewUrl: string | null
    downloadUrl?: string | null
    file?: {
      id: string
      size: number
      mimetype: string
    }
  }
  folder?: {
    id: string
    name: string
  }
}

const isImageFile = (mimetype: string): boolean => getPreviewFileType(mimetype) === 'image'

type ListRow =
  | { type: 'go-up'; data: null; name: string; createdBy: string; updatedAt: string; size: number }
  | { type: 'folder'; data: FolderType; name: string; createdBy: string; updatedAt: string; size: number }
  | { type: 'document'; data: Document; name: string; createdBy: string; updatedAt: string; size: number }

interface DocumentListProps {
  folders: FolderType[]
  documents: Document[]
  loading: boolean
  isSearchResults?: boolean
  currentFolderId: string | null
  parentFolderId: string | null
  onFolderClick: (folderId: string | null) => void
  onDocumentClick: (documentId: string) => void
  /** Opens the in-app viewer; the parent decides what is previewable (Freelo #32223790). */
  onDocumentPreview?: (documentId: string) => void
  /** Opens the detail dialog; falls back to `onDocumentClick` when absent. */
  onDocumentDetails?: (documentId: string) => void
  onRefresh: () => void
  onEditFolder?: (folderId: string) => void
  onDeleteFolder?: (folder: { id: string; name: string }) => void
  onShareFolder?: (folder: FolderType) => void
  onManageAccess?: (folder: FolderType) => void
  onDownloadFolder?: (folder: FolderType) => void
  onDownloadDocument?: (document: Document) => void
  onDeleteDocument?: (document: Document) => void
  onShareDocument?: (document: Document) => void
  onCopyFolderLink?: (folder: FolderType) => void
  onCopyDocumentLink?: (document: Document) => void
  onMoveFolder?: (folder: { id: string; name: string }) => void
  onMoveDocument?: (document: { id: string; name: string }) => void
  selection?: string[]
  onSelectionChange?: (selection: string[]) => void
}

const DocumentList: React.FC<DocumentListProps> = ({
  folders,
  documents,
  loading,
  isSearchResults,
  currentFolderId,
  parentFolderId,
  onFolderClick,
  onDocumentClick,
  onDocumentPreview,
  onDocumentDetails,
  onRefresh,
  onEditFolder,
  onDeleteFolder,
  onShareFolder,
  onManageAccess,
  onDownloadFolder,
  onDownloadDocument,
  onDeleteDocument,
  onShareDocument,
  onCopyFolderLink,
  onCopyDocumentLink,
  onMoveFolder,
  onMoveDocument,
  selection = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation()
  const openDetails = onDocumentDetails ?? onDocumentClick

  const gridSelection = useMemo(() => {
    return selection.map((id) => {
      if (id.startsWith('folder-') || id.startsWith('doc-')) return id
      return `doc-${id}`
    })
  }, [selection])

  const handleGridSelectionChange = (newGridSelection: string[]) => {
    if (!onSelectionChange) return
    onSelectionChange(newGridSelection)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const showGoUp = currentFolderId && !isSearchResults

  const rows: ListRow[] = []

  // "Go up" as a table row
  if (showGoUp) {
    rows.push({
      type: 'go-up',
      data: null,
      name: '..',
      createdBy: '',
      updatedAt: '',
      size: 0,
    })
  }

  if (!isSearchResults) {
    folders.forEach((folder) => {
      rows.push({
        type: 'folder',
        data: folder,
        name: folder.name,
        createdBy: '',
        updatedAt: '',
        size: 0,
      })
    })
  }

  documents.forEach((doc) => {
    rows.push({
      type: 'document',
      data: doc,
      name: doc.name,
      createdBy: doc.createdBy?.name || '',
      updatedAt: doc.updatedAt ?? doc.createdAt ?? '',
      size: doc.currentRevision?.file?.size ?? doc.size ?? 0,
    })
  })

  if (rows.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-12">
        <File className="h-10 w-10 mb-3 mx-auto opacity-40" />
        <p>{isSearchResults ? t('DocControl_no_search_results') : t('DocControl_no_folders_yet')}</p>
      </div>
    )
  }

  const columns = [
    { name: 'name', title: t('DocControl_name'), width: isSearchResults ? '40%' : '50%' },
    ...(isSearchResults ? [{ name: 'folder', title: t('DocControl_folder'), width: '15%' }] : []),
    { name: 'createdBy', title: t('DocControl_created_by'), width: '15%' },
    { name: 'updatedAt', title: t('DocControl_modified'), width: '20%' },
    { name: 'size', title: t('DocControl_size'), width: '10%' },
  ]

  const formatters = [
    {
      formatter: ({ row }: { row: ListRow }) => {
        if (row.type === 'go-up') {
          return (
            <div className="flex items-center gap-2">
              <CornerLeftUp className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium">..</span>
              <span className="text-muted-foreground text-xs">{t('DocControl_go_up')}</span>
            </div>
          )
        }
        if (row.type === 'folder') {
          return (
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="font-medium truncate">{row.data.name}</span>
              {row.data.activeShareLinkCount > 0 && (
                <Share2 className="h-3 w-3 text-cyan-500 shrink-0" />
              )}
              {row.data.documentCount > 0 && (
                <span className="text-muted-foreground text-xs">({row.data.documentCount})</span>
              )}
            </div>
          )
        }
        const doc = row.data
        const canPreview = !!onDocumentPreview && canPreviewDocument(doc)
        const showThumbnail = canPreview && doc.previewUrl && isImageFile(doc.mimetype)
        return (
          <div className="flex items-center gap-2">
            {showThumbnail ? (
              <img
                src={doc.previewUrl!}
                alt={doc.name}
                className="h-7 w-7 object-cover rounded shrink-0 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  onDocumentPreview!(doc.id)
                }}
                title={t('DocControl_click_to_enlarge')}
              />
            ) : canPreview ? (
              <FileTypeIcon
                filename={doc.filename}
                size={20}
                className="cursor-pointer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  onDocumentPreview!(doc.id)
                }}
              />
            ) : (
              <FileTypeIcon filename={doc.filename} size={20} />
            )}
            <span className="font-medium truncate">{doc.name}</span>
            {doc.currentRevision && doc.currentRevision.version > 1 && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                v{doc.currentRevision.version}
              </Badge>
            )}
            {doc.activeShareLinkCount > 0 && (
              <Share2 className="h-3 w-3 text-cyan-500 shrink-0" />
            )}
          </div>
        )
      },
      for: ['name'],
    },
    ...(isSearchResults ? [{
      formatter: ({ row }: { row: ListRow }) => {
        if (row.type !== 'document') return null
        const doc = row.data
        return doc.folder ? (
          <Badge variant="secondary">
            <Folder className="h-3 w-3 text-amber-500" />
            {doc.folder.name}
          </Badge>
        ) : null
      },
      for: ['folder'],
    }] : []),
    {
      formatter: ({ row }: { row: ListRow }) => {
        if (row.type !== 'document') return null
        return <span className="text-sm">{row.data.createdBy?.name}</span>
      },
      for: ['createdBy'],
    },
    {
      formatter: ({ row }: { row: ListRow }) => {
        if (row.type !== 'document') return null
        return <span className="text-sm">{formatDate(row.data.updatedAt)}</span>
      },
      for: ['updatedAt'],
    },
    {
      formatter: ({ row }: { row: ListRow }) => {
        if (row.type !== 'document') return null
        const size = row.data.currentRevision?.file?.size ?? row.data.size
        if (size === undefined || size === null) return null
        return <span className="text-sm">{formatBytes(size)}</span>
      },
      for: ['size'],
    },
  ]

  const rowActions = [
    {
      title: t('Common_open'),
      show: (row: ListRow) => row.type === 'folder',
      action: (row: ListRow) => row.type === 'folder' && onFolderClick(row.data.id),
    },
    ...(onEditFolder ? [{
      title: t('Common_edit'),
      show: (row: ListRow) => row.type === 'folder' && rowLevelAtLeast(row.data, 'EDITOR'),
      action: (row: ListRow) => row.type === 'folder' && onEditFolder(row.data.id),
    }] : []),
    ...(onShareFolder ? [{
      title: t('DocControl_share'),
      divider: true,
      show: (row: ListRow) => row.type === 'folder' && rowLevelAtLeast(row.data, 'ADMIN'),
      action: (row: ListRow) => row.type === 'folder' && onShareFolder(row.data),
    }] : []),
    ...(onManageAccess ? [{
      title: t('DocControl_manage_access', 'Manage access'),
      show: (row: ListRow) => row.type === 'folder',
      action: (row: ListRow) => row.type === 'folder' && onManageAccess(row.data),
    }] : []),
    ...(onCopyFolderLink ? [{
      title: t('DocControl_copy_link'),
      show: (row: ListRow) => row.type === 'folder',
      action: (row: ListRow) => row.type === 'folder' && onCopyFolderLink(row.data),
    }] : []),
    ...(onDownloadFolder ? [{
      title: t('DocControl_download_folder'),
      show: (row: ListRow) => row.type === 'folder',
      action: (row: ListRow) => row.type === 'folder' && onDownloadFolder(row.data),
    }] : []),
    ...(onMoveFolder ? [{
      title: t('DocControl_move_to'),
      show: (row: ListRow) => row.type === 'folder' && rowLevelAtLeast(row.data, 'EDITOR'),
      action: (row: ListRow) => row.type === 'folder' && onMoveFolder({ id: row.data.id, name: row.data.name }),
    }] : []),
    ...(onDeleteFolder ? [{
      title: t('Common_delete'),
      color: 'danger',
      divider: true,
      show: (row: ListRow) => row.type === 'folder' && rowLevelAtLeast(row.data, 'MANAGER'),
      action: (row: ListRow) => row.type === 'folder' && onDeleteFolder({ id: row.data.id, name: row.data.name }),
    }] : []),
    ...(onDocumentPreview ? [{
      title: t('DocControl_preview'),
      show: (row: ListRow) => row.type === 'document' && canPreviewDocument(row.data),
      action: (row: ListRow) => row.type === 'document' && onDocumentPreview(row.data.id),
    }] : []),
    {
      title: t('DocControl_view_details'),
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && openDetails(row.data.id),
    },
    ...(onDownloadDocument ? [{
      title: t('Common_download'),
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && onDownloadDocument(row.data),
    }] : []),
    ...(onShareDocument ? [{
      title: t('DocControl_share'),
      divider: true,
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && onShareDocument(row.data),
    }] : []),
    ...(onCopyDocumentLink ? [{
      title: t('DocControl_copy_link'),
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && onCopyDocumentLink(row.data),
    }] : []),
    ...(onMoveDocument ? [{
      title: t('DocControl_move_to'),
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && onMoveDocument({ id: row.data.id, name: row.data.name }),
    }] : []),
    ...(onDeleteDocument ? [{
      title: t('Common_delete'),
      color: 'danger',
      divider: true,
      show: (row: ListRow) => row.type === 'document',
      action: (row: ListRow) => row.type === 'document' && onDeleteDocument(row.data),
    }] : []),
  ]

  const handleRowClick = (row: ListRow) => {
    if (row.type === 'go-up') {
      onFolderClick(parentFolderId)
    } else if (row.type === 'folder') {
      onFolderClick(row.data.id)
    } else {
      onDocumentClick(row.data.id)
    }
  }

  return (
    <>
      <Grid
        columns={columns}
        rows={rows}
        formatters={formatters}
        rowActions={rowActions}
        onRowClick={handleRowClick}
        getRowId={(row: ListRow) =>
          row.type === 'go-up' ? 'go-up' : row.type === 'folder' ? `folder-${row.data.id}` : `doc-${row.data.id}`
        }
        isRowSelectable={(row: ListRow) => row.type !== 'go-up'}
        sorting
        showHeader
        selection={onSelectionChange ? gridSelection : undefined}
        onSelectionChange={onSelectionChange ? handleGridSelectionChange : undefined}
        showSelectionColumn={!!onSelectionChange}
      />
    </>
  )
}

export default DocumentList
